"use client"
import { Link } from "react-router-dom"
import Footer from "./Footer"

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Privacy <span className="hero-highlight">Policy</span>
            </h1>
            <p className="hero-description">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information
              when you use Shopify Scraper.
            </p>
            <p className="last-updated">Last updated: December 19, 2024</p>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="content-section">
        <div className="content-container">
          <div className="privacy-content">
            {/* Information We Collect */}
            <div className="section">
              <h2 className="section-title">Information We Collect</h2>
              <div className="subsection">
                <h3 className="subsection-title">Personal Information</h3>
                <p className="text">When you create an account or use our services, we may collect:</p>
                <ul className="list">
                  <li>Email address for account creation and communication</li>
                  <li>Name and contact information when provided</li>
                  <li>
                    Payment information for subscription services (processed securely through third-party providers)
                  </li>
                  <li>Support communications and feedback</li>
                </ul>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">Usage Information</h3>
                <p className="text">We automatically collect certain information about how you use our extension:</p>
                <ul className="list">
                  <li>Extension usage statistics and performance data</li>
                  <li>Error logs and crash reports to improve our service</li>
                  <li>Feature usage patterns to enhance user experience</li>
                  <li>Browser type and version for compatibility purposes</li>
                </ul>
              </div>
            </div>

            {/* How We Use Information */}
            <div className="section">
              <h2 className="section-title">How We Use Your Information</h2>
              <p className="text">We use the collected information to:</p>
              <ul className="list">
                <li>
                  <strong>Provide and improve our services:</strong> Ensure the extension works properly and add new
                  features
                </li>
                <li>
                  <strong>Customer support:</strong> Respond to your questions and resolve technical issues
                </li>
                <li>
                  <strong>Account management:</strong> Manage your subscription and send important updates
                </li>
                <li>
                  <strong>Security:</strong> Protect against fraud and unauthorized access
                </li>
                <li>
                  <strong>Legal compliance:</strong> Meet our legal obligations and enforce our terms
                </li>
              </ul>
            </div>

            {/* Data Sharing */}
            <div className="section">
              <h2 className="section-title">Information Sharing and Disclosure</h2>
              <p className="text">
                We do not sell, trade, or rent your personal information to third parties. We may share your information
                only in these limited circumstances:
              </p>
              <ul className="list">
                <li>
                  <strong>Service providers:</strong> Trusted third-party services that help us operate our business
                  (payment processors, hosting providers)
                </li>
                <li>
                  <strong>Legal requirements:</strong> When required by law or to protect our rights and safety
                </li>
                <li>
                  <strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets
                </li>
                <li>
                  <strong>With your consent:</strong> When you explicitly agree to share information
                </li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="section">
              <h2 className="section-title">Data Security</h2>
              <p className="text">
                We implement appropriate technical and organizational measures to protect your personal information:
              </p>
              <ul className="list">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Limited access to personal information on a need-to-know basis</li>
                <li>Secure payment processing through certified providers</li>
              </ul>
              <div className="notice">
                <p>
                  <strong>Important:</strong> While we strive to protect your information, no method of transmission
                  over the internet is 100% secure. We cannot guarantee absolute security.
                </p>
              </div>
            </div>

            {/* Data Retention */}
            <div className="section">
              <h2 className="section-title">Data Retention</h2>
              <p className="text">We retain your personal information only as long as necessary to:</p>
              <ul className="list">
                <li>Provide our services to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Improve our services through analytics</li>
              </ul>
              <p className="text">
                When you delete your account, we will delete or anonymize your personal information within 30 days,
                except where we are required to retain it for legal purposes.
              </p>
            </div>

            {/* Your Rights */}
            <div className="section">
              <h2 className="section-title">Your Rights and Choices</h2>
              <p className="text">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list">
                <li>
                  <strong>Access:</strong> Request a copy of the personal information we hold about you
                </li>
                <li>
                  <strong>Correction:</strong> Request correction of inaccurate or incomplete information
                </li>
                <li>
                  <strong>Deletion:</strong> Request deletion of your personal information
                </li>
                <li>
                  <strong>Portability:</strong> Request transfer of your data to another service
                </li>
                <li>
                  <strong>Objection:</strong> Object to certain processing of your information
                </li>
                <li>
                  <strong>Restriction:</strong> Request limitation of how we process your information
                </li>
              </ul>
              <p className="text">
                To exercise these rights, please contact us at{" "}
                <a href="mailto:privacy@shopifyscraper.com" className="link">
                  privacy@shopifyscraper.com
                </a>
                .
              </p>
            </div>

            {/* Cookies and Tracking */}
            <div className="section">
              <h2 className="section-title">Cookies and Tracking Technologies</h2>
              <p className="text">Our extension and website use cookies and similar technologies to:</p>
              <ul className="list">
                <li>Remember your preferences and settings</li>
                <li>Analyze usage patterns and improve performance</li>
                <li>Provide personalized experiences</li>
                <li>Ensure security and prevent fraud</li>
              </ul>
              <p className="text">
                You can control cookies through your browser settings, but disabling them may affect the functionality
                of our services.
              </p>
            </div>

            {/* Third-Party Services */}
            <div className="section">
              <h2 className="section-title">Third-Party Services</h2>
              <p className="text">
                Our extension may interact with third-party websites and services. This privacy policy does not apply
                to:
              </p>
              <ul className="list">
                <li>Shopify stores you visit while using our extension</li>
                <li>Other websites or services linked from our platform</li>
                <li>Third-party payment processors</li>
              </ul>
              <p className="text">
                We encourage you to review the privacy policies of any third-party services you use.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="section">
              <h2 className="section-title">Children's Privacy</h2>
              <p className="text">
                Our services are not intended for children under 13 years of age. We do not knowingly collect personal
                information from children under 13. If you believe we have collected information from a child under 13,
                please contact us immediately.
              </p>
            </div>

            {/* International Users */}
            <div className="section">
              <h2 className="section-title">International Data Transfers</h2>
              <p className="text">
                Your information may be transferred to and processed in countries other than your own. We ensure
                appropriate safeguards are in place to protect your information in accordance with applicable data
                protection laws.
              </p>
            </div>

            {/* Changes to Policy */}
            <div className="section">
              <h2 className="section-title">Changes to This Privacy Policy</h2>
              <p className="text">
                We may update this privacy policy from time to time. We will notify you of any material changes by:
              </p>
              <ul className="list">
                <li>Posting the updated policy on our website</li>
                <li>Sending an email notification to registered users</li>
                <li>Displaying a notice in our extension</li>
              </ul>
              <p className="text">
                Your continued use of our services after any changes indicates your acceptance of the updated policy.
              </p>
            </div>

            {/* Contact Information */}
            <div className="section">
              <h2 className="section-title">Contact Us</h2>
              <p className="text">
                If you have any questions about this privacy policy or our data practices, please contact us:
              </p>
              <div className="contact-info">
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:privacy@shopifyscraper.com" className="link">
                    privacy@shopifyscraper.com
                  </a>
                </p>
                <p>
                  <strong>Support:</strong>{" "}
                  <a href="mailto:support@shopifyscraper.com" className="link">
                    support@shopifyscraper.com
                  </a>
                </p>
                <p>
                  <strong>Website:</strong>{" "}
                  <a href="/" className="link">
                    www.shopifyscraper.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Questions about our privacy practices?</h2>
          <p className="cta-description">
            We're here to help. Contact our support team for any privacy-related questions.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="cta-button primary">
              Contact Support
            </Link>
            <Link to="/" className="cta-button secondary">
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Styling */}
      <style jsx="true">{`
        /* Hero Section */
        .hero-section {
          padding: 4rem 0 2rem;
          background-color: #fff;
        }
        
        .hero-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
          text-align: center;
        }
        
        .hero-title {
          font-size: 2.5rem;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          color: #1a202c;
        }
        
        .hero-highlight {
          color: #007BFF;
        }
        
        .hero-description {
          font-size: 1.125rem;
          line-height: 1.6;
          color: #4a5568;
          margin-bottom: 1rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }
        
        .last-updated {
          font-size: 0.875rem;
          color: #718096;
          font-style: italic;
        }

        /* Content Section */
        .content-section {
          padding: 2rem 0 4rem;
          background-color: #f9fafb;
        }
        
        .content-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .privacy-content {
          background-color: #fff;
          padding: 3rem;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        
        .section {
          margin-bottom: 3rem;
        }
        
        .section:last-child {
          margin-bottom: 0;
        }
        
        .section-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1a202c;
          margin-bottom: 1.5rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #007BFF;
        }
        
        .subsection {
          margin-bottom: 2rem;
        }
        
        .subsection-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 1rem;
        }
        
        .text {
          font-size: 1rem;
          line-height: 1.7;
          color: #4a5568;
          margin-bottom: 1rem;
        }
        
        .list {
          margin: 1rem 0;
          padding-left: 1.5rem;
        }
        
        .list li {
          font-size: 1rem;
          line-height: 1.7;
          color: #4a5568;
          margin-bottom: 0.5rem;
        }
        
        .notice {
          background-color: #fef5e7;
          border-left: 4px solid #f6ad55;
          padding: 1rem;
          margin: 1.5rem 0;
          border-radius: 0 0.375rem 0.375rem 0;
        }
        
        .notice p {
          margin: 0;
          color: #744210;
        }
        
        .contact-info {
          background-color: #f7fafc;
          padding: 1.5rem;
          border-radius: 0.375rem;
          margin-top: 1rem;
        }
        
        .contact-info p {
          margin-bottom: 0.5rem;
          font-size: 1rem;
          color: #4a5568;
        }
        
        .contact-info p:last-child {
          margin-bottom: 0;
        }
        
        .link {
          color: #007BFF;
          text-decoration: none;
          font-weight: 500;
        }
        
        .link:hover {
          text-decoration: underline;
        }

        /* CTA Section */
        .cta-section {
          padding: 4rem 0;
          background-color: #007BFF;
          color: white;
        }
        
        .cta-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
          text-align: center;
        }
        
        .cta-title {
          font-size: 2.25rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }
        
        .cta-description {
          font-size: 1.125rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }
        
        .cta-buttons {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
        }
        
        .cta-button {
          padding: 0.75rem 1.5rem;
          border-radius: 0.375rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }
        
        .cta-button.primary {
          background-color: white;
          color: #007BFF;
        }
        
        .cta-button.primary:hover {
          background-color: #f9fafb;
        }
        
        .cta-button.secondary {
          background-color: transparent;
          border: 2px solid white;
          color: white;
        }
        
        .cta-button.secondary:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        /* Responsive Styles */
        @media (max-width: 768px) {
          .hero-title {
            font-size: 2rem;
          }
          
          .cta-title {
            font-size: 1.75rem;
          }
          
          .privacy-content {
            padding: 2rem 1.5rem;
          }
          
          .section-title {
            font-size: 1.25rem;
          }
          
          .subsection-title {
            font-size: 1.125rem;
          }
        }
        
        @media (max-width: 480px) {
          .privacy-content {
            padding: 1.5rem 1rem;
          }
          
          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }
          
          .cta-button {
            width: 100%;
            max-width: 250px;
          }
        }
      `}</style>
    </div>
  )
}

export default PrivacyPolicy
